import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngxs/store';
import { SetAuthAction, ValidateTokenAction } from '@tsin-core/actions/auth.action';
import { AuthState } from '@tsin-core/states/auth.state';
import { determineAuthStatus, isStaffOrAdminRole } from '@tsin-shared/utils/utils';
import { ConfigService } from '@tsin-core/services/common/config.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AppLocale, LocaleService } from '@tsin-core/services/common/locale.service';
import { CognitoAuthService } from '@tsin-core/services/auth/cognito-auth.service';

@Component({
  selector: 'app-callback',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  template: `
    <div class="callback-container">
      <div class="callback-content">
        <div class="spinner"></div>
        <h2>{{ 'ACCOUNT.CALLBACK.AUTHENTICATING' | translate }}</h2>
        <p>{{ message }}</p>
        @if (error) {
          <div class="error-message">
            <p>{{ error }}</p>
            <button (click)="retryLogin()">{{ 'ACCOUNT.CALLBACK.RETRY' | translate }}</button>
          </div>
        }
      </div>
    </div>
  `,
  styles: [`
    .callback-container {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    }

    .callback-content {
      background: white;
      padding: 3rem;
      border-radius: 12px;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
      text-align: center;
      max-width: 400px;
      width: 90%;
    }

    .spinner {
      border: 4px solid #f3f3f3;
      border-top: 4px solid #667eea;
      border-radius: 50%;
      width: 50px;
      height: 50px;
      animation: spin 1s linear infinite;
      margin: 0 auto 1.5rem;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    h2 {
      color: #333;
      margin-bottom: 1rem;
      font-size: 1.5rem;
    }

    p {
      color: #666;
      margin-bottom: 1rem;
    }

    .error-message {
      margin-top: 1.5rem;
      padding: 1rem;
      background: #fee;
      border-radius: 8px;
      border-left: 4px solid #f44;
    }

    .error-message p {
      color: #c33;
      font-weight: 500;
    }

    button {
      margin-top: 1rem;
      padding: 0.75rem 1.5rem;
      background: #667eea;
      color: white;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      font-size: 1rem;
      transition: background 0.3s;
    }

    button:hover {
      background: #5568d3;
    }
  `]
})
export class CallbackComponent implements OnInit {
  message = '';
  error: string | null = null;

  constructor(
    private router: Router,
    private http: HttpClient,
    private store: Store,
    private configService: ConfigService,
    private translate: TranslateService,
    private localeService: LocaleService,
    private cognitoAuthService: CognitoAuthService,
  ) {}

  private t(key: string): string {
    return this.translate.instant(key);
  }

  ngOnInit(): void {
    this.message = this.t('ACCOUNT.CALLBACK.PROCESSING');
    console.log('Auth Callback Component initialized');
    this.handleCallback();
  }

  private async handleCallback(): Promise<void> {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const oauthError = urlParams.get('error');

    // ── Path 1: Cognito returned an error (e.g. access_denied) ───────────────
    if (oauthError) {
      console.error('Cognito returned an error:', oauthError, urlParams.get('error_description'));
      this.error = urlParams.get('error_description') ?? oauthError;
      this.message = this.t('ACCOUNT.CALLBACK.FAILED');
      return;
    }

    // ── Path 2: PKCE direct flow — ?code= present ────────────────────────────
    if (code) {
      await this.handlePkceCallback(code);
      return;
    }

    // ── Path 3: Legacy cookie flow — id_token cookie set by centralized auth-ui
    const cookieIdToken = this.getCookie('id_token');
    if (cookieIdToken) {
      await this.handleCookieCallback(cookieIdToken);
      return;
    }

    // ── Path 4: SSO token hand-off — ?idToken= from centralized auth-ui ───────
    const ssoIdToken = urlParams.get('idToken');
    if (ssoIdToken) {
      await this.handleSsoCallback(ssoIdToken);
      return;
    }

    console.error('No ?code=, no id_token cookie, and no ?idToken= found');
    this.error = this.t('ACCOUNT.CALLBACK.NO_TOKEN');
    this.message = this.t('ACCOUNT.CALLBACK.FAILED');
  }

  /**
   * PKCE direct flow: exchange the Cognito authorization code for tokens,
   * then pass the id_token to the backend token-exchange endpoint to get a
   * PRO-service JWT. Shares the same post-exchange enrichment as the cookie path.
   */
  private async handlePkceCallback(code: string): Promise<void> {
    try {
      console.log('PKCE code received, exchanging with Cognito token endpoint...');
      this.message = this.t('ACCOUNT.CALLBACK.EXCHANGING_TOKEN');

      const tokens = await this.cognitoAuthService.exchangeCodeForTokens(code);
      console.log('Cognito PKCE exchange successful, obtaining PRO JWT...');

      const authServiceUrl = this.configService.getAuthServiceUrl() || 'https://auth.api.stage.tsin.ca';
      const response: any = await this.http.post(
        `${authServiceUrl}/api/auth/cognito/token-exchange`,
        { cognitoIdToken: tokens.idToken, service: 'PRO' }
      ).toPromise();

      await this.processTokenExchangeResponse(response, tokens.refreshToken);
    } catch (err: any) {
      console.error('PKCE callback error:', err);
      this.error = err.message || this.t('COMMON.TOAST.GENERIC_ERROR');
      this.message = this.t('ACCOUNT.CALLBACK.FAILED');
    }
  }

  /**
   * SSO redirect flow: the centralized auth-ui passed an id_token via ?idToken=
   * query parameter after the user authenticated there. Exchange it for a PRO JWT.
   */
  private async handleSsoCallback(idToken: string): Promise<void> {
    try {
      console.log('SSO idToken received via query param, exchanging for PRO service JWT...');
      this.message = this.t('ACCOUNT.CALLBACK.EXCHANGING_TOKEN');

      const authServiceUrl = this.configService.getAuthServiceUrl() || 'https://auth.api.dev.tsinconduit.ca';
      const response: any = await this.http.post(
        `${authServiceUrl}/api/auth/cognito/token-exchange`,
        { cognitoIdToken: idToken, service: 'PRO' }
      ).toPromise();

      await this.processTokenExchangeResponse(response, null);
    } catch (error: any) {
      console.error('SSO callback error:', error);
      this.error = error.message || this.t('COMMON.TOAST.GENERIC_ERROR');
      this.message = this.t('ACCOUNT.CALLBACK.FAILED');
    }
  }

  /**
   * Legacy cookie flow: read the id_token cookie set by the centralized auth-ui
   * and exchange it for a PRO-service JWT.
   */
  private async handleCookieCallback(idToken: string): Promise<void> {
    try {
      console.log('id_token cookie found, exchanging for PRO service JWT...');
      this.message = this.t('ACCOUNT.CALLBACK.EXCHANGING_TOKEN');

      const authServiceUrl = this.configService.getAuthServiceUrl() || 'https://auth.api.stage.tsin.ca';
      const response: any = await this.http.post(
        `${authServiceUrl}/api/auth/cognito/token-exchange`,
        { cognitoIdToken: idToken, service: 'PRO' }
      ).toPromise();

      await this.processTokenExchangeResponse(response, null);
    } catch (error: any) {
      console.error('Cookie callback error:', error);
      this.error = error.message || this.t('COMMON.TOAST.GENERIC_ERROR');
      this.message = this.t('ACCOUNT.CALLBACK.FAILED');
    }
  }

  /**
   * Shared post-exchange logic: validate the backend response, enrich the user
   * with the PRO role, set NGXS state, and navigate to the correct route.
   *
   * @param response       Raw backend token-exchange response
   * @param cognitoRefresh Cognito refresh token (available on PKCE path; null on cookie path)
   */
  private async processTokenExchangeResponse(response: any, cognitoRefresh: string | null): Promise<void> {
    const token = response?.data?.token ?? response?.token;
    const user  = response?.data?.user  ?? response?.user;
    // Prefer the Cognito refresh token (longer-lived) if available; fall back to
    // whatever the auth-service returns (for backward compatibility with cookie path).
    const refreshToken = cognitoRefresh ?? response?.data?.refreshToken ?? response?.refreshToken ?? null;
    const success = response?.success ?? false;

    if (!success || !token) {
      throw new Error(response?.message ?? 'Token exchange failed: no token in response');
    }

    console.log('Token exchange successful');

    localStorage.setItem('token', token);
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    }

    this.message = this.t('ACCOUNT.CALLBACK.SUCCESS');

    const enrichedUser = this.extractProRole(user);
    console.log('Enriched user with PRO role:', enrichedUser?.role);

    // 1. Set auth state immediately so guards pass on navigation
    this.store.dispatch(new SetAuthAction({ token, refreshToken, user: enrichedUser }));

    // Detect locale from URL (e.g. /fr/auth/callback → 'fr')
    const urlLocale: AppLocale = window.location.pathname.startsWith('/fr') ? 'fr' : 'en';

    // 2. Fetch the real user profile from the PRO backend (/user/me)
    try {
      await this.store.dispatch(new ValidateTokenAction({ token, refreshToken })).toPromise();
      const realUser = this.store.selectSnapshot(AuthState.getCurrentUser);
      if (realUser) {
        const lang = this.resolveLocale(realUser, urlLocale);
        this.localeService.setLocale(lang);
        const baseUrl = determineAuthStatus(realUser);
        const url = lang === 'fr' ? '/fr' + baseUrl : baseUrl;
        console.log('Navigating to (with PRO profile):', url);
        this.router.navigate([url]);
      } else {
        console.warn('PRO /user/me returned no user, using auth-service data');
        this.navigateWithFallbackUser(enrichedUser, urlLocale);
      }
    } catch (profileError) {
      console.warn('PRO profile fetch failed, navigating with auth-service data:', profileError);
      this.navigateWithFallbackUser(enrichedUser, urlLocale);
    }
  }

  private navigateWithFallbackUser(user: any, urlLocale: AppLocale): void {
    const lang = this.resolveLocale(user, urlLocale);
    this.localeService.setLocale(lang);
    const baseUrl = user ? determineAuthStatus(user) : '/user';
    const url = lang === 'fr' ? '/fr' + baseUrl : baseUrl;
    this.router.navigate([url]);
  }

  /**
   * Extract the PRO-specific role from the central auth service response.
   * Global superAdmin always takes priority over any service-specific role.
   */
  private extractProRole(user: any): any {
    if (!user) return user;

    // Global superAdmin check: if any profile has superAdmin, honour it globally
    if (Array.isArray(user.profiles) && user.profiles.some((p: any) => p.role === 'superAdmin')) {
      return { ...user, role: 'superAdmin' };
    }

    // Check roles array for superAdmin
    if (Array.isArray(user.roles) && user.roles.includes('superAdmin')) {
      return { ...user, role: 'superAdmin' };
    }

    if (Array.isArray(user.profiles) && user.profiles.length > 0) {
      const proProfile = user.profiles.find((p: any) => p.project === 'PRO');
      if (proProfile?.role) {
        return { ...user, role: proProfile.role };
      }
      if (user.profiles[0]?.role) {
        return { ...user, role: user.profiles[0].role };
      }
    }

    if (Array.isArray(user.roles) && user.roles.length > 0) {
      return { ...user, role: user.roles[0] };
    }

    return user;
  }

  /**
   * Determine which locale to use after login.
   * - Admin/staff: use the locale from the URL (so /fr/auth/callback → French).
   * - Candidates: use their stored preferredLanguage (locked at registration).
   *
   * Déterminer la langue à utiliser après la connexion.
   * - Admin/personnel : utiliser la langue de l'URL.
   * - Candidats : utiliser leur langue d'inscription.
   */
  private resolveLocale(user: any, urlLocale: AppLocale): AppLocale {
    if (isStaffOrAdminRole(user?.role)) {
      return urlLocale;
    }
    return (user?.preferredLanguage ?? urlLocale) as AppLocale;
  }

  /** Read a cookie value by name. */
  private getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  retryLogin(): void {
    // Prefer PKCE direct flow if configured; fall back to centralized auth-ui
    if (this.cognitoAuthService.isConfigured()) {
      const isFr = window.location.pathname.startsWith('/fr');
      const returnPath = isFr ? '/fr' : '/';
      this.cognitoAuthService.initiateLogin(returnPath);
      return;
    }

    const authUiUrl = this.configService.getAuthUiUrl();
    if (authUiUrl) {
      const isFr = window.location.pathname.startsWith('/fr');
      const callbackPath = isFr ? '/fr/auth/callback' : '/auth/callback';
      const returnTo = window.location.origin + callbackPath;
      const loginPath = isFr ? '/fr/login' : '/login';
      window.location.href = `${authUiUrl}${loginPath}?returnTo=${encodeURIComponent(returnTo)}`;
    } else {
      this.router.navigate(['/account/validate-token']);
    }
  }
}
